import styled from '@emotion/styled';
import type { FunctionComponent, PropsWithChildren } from 'react';
import type { Theme } from '@emotion/react';
import { ThemeProvider as ArtemisThemeProvider } from '@artemis-ui/components';
import { darkTheme, lightTheme } from '@artemis-ui/foundations';
import { LogzioTheme } from '@logz-pkg/enums';
import { useSessionState } from 'ui/state/hooks/session.state.hook';

// `display: contents` so the wrapper generates no box and only passes Artemis' font down by inheritance —
// gaia's body font is Roboto and Artemis components are spaced against Poppins' metrics.
const ArtemisFontScope = styled.div`
  display: contents;
  font-family: ${({ theme }) => theme.fonts?.main};
`;

export const ArtemisThemeBoundary: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { userSettings } = useSessionState();
  const isDark = userSettings?.logzioTheme === LogzioTheme.Dark;

  // Both design systems augment @emotion/react's `Theme` globally, so the single `Theme` type demands gaia's
  // fields AND Artemis'. Neither theme object satisfies both, and the shapes diverge enough to need the double
  // assertion. At runtime each provider is correct for its own subtree.
  return (
    <ArtemisThemeProvider theme={(isDark ? darkTheme : lightTheme) as unknown as Theme} withGlobalStyles={false}>
      <ArtemisFontScope>{children}</ArtemisFontScope>
    </ArtemisThemeProvider>
  );
};
